import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, LogOut, Search, Phone, Mail, MessageCircle, FileText, TrendingUp,
  Users, Flame, CalendarClock, PiggyBank, ChevronDown, RefreshCw, Loader2, Download,
  Settings, Send, CheckCircle2, AlertTriangle, Trash2,
} from "lucide-react";
import { adminMe, adminLeads, adminStats, adminBillFile, adminBillDownload,
  adminGetSettings, adminUpdateSettings, adminEmailLogs, adminTestNotification, adminDeleteLead,
  clearToken, getToken } from "../lib/api";
import { LeadResearch } from "./LeadResearch";

const CAT = {
  Hoch: { badge: "bg-rose-50 text-rose-700 border-rose-200", ring: "text-rose-600", dot: "bg-rose-500" },
  Mittel: { badge: "bg-amber-50 text-amber-700 border-amber-200", ring: "text-amber-600", dot: "bg-amber-500" },
  Niedrig: { badge: "bg-slate-100 text-slate-600 border-slate-200", ring: "text-slate-500", dot: "bg-slate-400" },
};

const TYPE_LABEL = {
  beratung: "Beratung", rueckruf: "Rückruf", termin: "Termin",
  kontakt: "Kontakt", "rechnung-upload": "Rechnungs-Upload",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};
const waLink = (phone) => {
  const d = (phone || "").replace(/[^\d]/g, "");
  return d ? `https://wa.me/${d.startsWith("0") ? "49" + d.slice(1) : d}` : null;
};

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className={`inline-grid place-items-center h-9 w-9 rounded-xl ${accent}`}><Icon className="h-4.5 w-4.5" /></span>
    </div>
    <p className="text-3xl font-heading font-semibold text-navy-900 leading-none">{value}</p>
    <p className="text-xs text-slate-500 mt-1.5">{label}</p>
  </div>
);

const LeadCard = ({ lead, onDelete }) => {
  const [open, setOpen] = useState(false);
  const c = CAT[lead.category] || CAT.Niedrig;
  const a = lead.analysis || {};
  const wa = waLink(lead.phone);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-[0_20px_40px_-30px_rgba(0,0,0,0.3)] transition-shadow" data-testid="lead-card">
      <div className="flex items-start gap-4">
        {/* Score */}
        <div className="shrink-0 text-center">
          <div className={`h-14 w-14 rounded-full border-2 grid place-items-center font-heading text-xl ${c.badge}`} data-testid="lead-score">{lead.score}</div>
          <p className="text-[10px] text-slate-400 mt-1">Score</p>
        </div>
        {/* Main */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span className="font-heading text-navy-900 text-lg truncate">{lead.name || "Ohne Namen"}</span>
            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${c.badge}`} data-testid="lead-category">{lead.category}</span>
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700" data-testid="lead-kundentyp">{lead.kundentyp}</span>
            {lead.has_bill && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 inline-flex items-center gap-1"><FileText className="h-3 w-3" /> Rechnung</span>}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mb-3">
            <span>{TYPE_LABEL[lead.type] || lead.type}</span>
            {lead.topic && <span className="capitalize">· {lead.topic}</span>}
            <span className="inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {fmtDate(lead.created_at)}</span>
          </div>
          {/* Reasons */}
          {lead.reasons?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {lead.reasons.map((r, i) => (
                <span key={i} className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">{r}</span>
              ))}
            </div>
          )}
          {/* Contact actions */}
          <div className="flex flex-wrap gap-2">
            {lead.phone && <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-slate-200 px-3 py-1.5 hover:bg-slate-50 text-navy-900" data-testid="lead-call"><Phone className="h-3.5 w-3.5" /> {lead.phone}</a>}
            {lead.email && <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-slate-200 px-3 py-1.5 hover:bg-slate-50 text-navy-900" data-testid="lead-email"><Mail className="h-3.5 w-3.5" /> {lead.email}</a>}
            {wa && <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs rounded-full border border-emerald-200 px-3 py-1.5 hover:bg-emerald-50 text-emerald-700" data-testid="lead-whatsapp"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>}
            {lead.has_bill && <button onClick={() => adminBillFile(lead.id).catch(() => {})} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-blue-200 px-3 py-1.5 hover:bg-blue-50 text-blue-700" data-testid="lead-view-bill"><FileText className="h-3.5 w-3.5" /> Rechnung ansehen</button>}
            {lead.has_bill && <button onClick={() => adminBillDownload(lead.id).catch(() => {})} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-blue-200 px-3 py-1.5 hover:bg-blue-50 text-blue-700" data-testid="lead-download-bill"><Download className="h-3.5 w-3.5" /> Download</button>}
            {(lead.message || Object.keys(a).length > 0) && (
              <button onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-slate-200 px-3 py-1.5 hover:bg-slate-50 text-slate-600 ml-auto" data-testid="lead-toggle-details">
                Details <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
            )}
            <button onClick={() => onDelete?.(lead)} className={`inline-flex items-center gap-1.5 text-xs rounded-full border border-slate-200 px-3 py-1.5 hover:bg-rose-50 hover:border-rose-200 text-slate-500 hover:text-rose-600 ${(lead.message || Object.keys(a).length > 0) ? "" : "ml-auto"}`} data-testid="lead-delete" title="Anfrage löschen">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          {/* Details */}
          {open && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-600 space-y-3" data-testid="lead-details">
              {lead.message && <p className="leading-relaxed">{lead.message}</p>}
              {Object.keys(a).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {a.anbieter && <div><p className="text-[10px] text-slate-400 uppercase">Anbieter</p><p className="text-navy-900">{a.anbieter}</p></div>}
                  {a.jahresverbrauch_kwh && <div><p className="text-[10px] text-slate-400 uppercase">Verbrauch</p><p className="text-navy-900">{a.jahresverbrauch_kwh} kWh</p></div>}
                  {a.jahreskosten_eur && <div><p className="text-[10px] text-slate-400 uppercase">Jahreskosten</p><p className="text-navy-900">{a.jahreskosten_eur} €</p></div>}
                  {a.arbeitspreis_ct_kwh && <div><p className="text-[10px] text-slate-400 uppercase">Arbeitspreis</p><p className="text-navy-900">{a.arbeitspreis_ct_kwh} ct/kWh</p></div>}
                  {a.einsparpotenzial_eur && <div><p className="text-[10px] text-slate-400 uppercase">Einsparpotenzial</p><p className="text-emerald-700 font-medium">≈ {a.einsparpotenzial_eur} €</p></div>}
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

const LOG_STATUS = {
  sent: { label: "Gesendet", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  failed: { label: "Fehlgeschlagen", cls: "bg-rose-50 text-rose-700 border-rose-200", icon: AlertTriangle },
  skipped: { label: "Übersprungen", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: AlertTriangle },
};

const SettingsView = () => {
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [sender, setSender] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [logs, setLogs] = useState([]);

  const loadLogs = async () => { try { setLogs(await adminEmailLogs()); } catch { /* noop */ } };

  useEffect(() => {
    (async () => {
      try {
        const s = await adminGetSettings();
        setEmail(s.notify_email || "");
        setSender(s.sender_email || "");
        await loadLogs();
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await adminUpdateSettings(email.trim());
      setEmail(r.notify_email);
      setMsg({ type: "ok", text: "Empfängeradresse gespeichert." });
    } catch (e) {
      setMsg({ type: "err", text: e?.response?.data?.detail || "Speichern fehlgeschlagen." });
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true); setMsg(null);
    try {
      const r = await adminTestNotification();
      const ok = r?.result?.status === "sent";
      setMsg(ok
        ? { type: "ok", text: `Test-E-Mail an ${r.recipient} gesendet. Bitte Posteingang prüfen.` }
        : { type: "err", text: `Versand nicht erfolgreich: ${r?.result?.error || "unbekannter Fehler"}` });
      await loadLogs();
    } catch (e) {
      setMsg({ type: "err", text: e?.response?.data?.detail || "Test-Versand fehlgeschlagen." });
    } finally { setTesting(false); }
  };

  if (loading) return <div className="grid place-items-center py-20" data-testid="settings-loading"><Loader2 className="h-7 w-7 animate-spin text-blue-700" /></div>;

  return (
    <div className="max-w-3xl space-y-8" data-testid="settings-view">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-heading font-semibold text-lg text-navy-900 mb-1">E-Mail-Benachrichtigungen</h2>
        <p className="text-sm text-slate-500 mb-5">Jede neue Anfrage (Formular, Landingpage, Rechnungs-Upload) wird zusätzlich an diese Adresse gesendet. Anfragen werden immer im Dashboard gespeichert – auch wenn der E-Mail-Versand fehlschlägt.</p>

        <label className="text-sm font-semibold text-slate-700">Empfängeradresse</label>
        <div className="mt-1.5 flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@domain.de"
            className="flex-1 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none px-4 py-2.5 text-sm"
            data-testid="settings-email-input"
          />
          <button onClick={save} disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-5 py-2.5 text-sm" data-testid="settings-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Speichern"}
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-2">Absender: {sender}</p>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button onClick={sendTest} disabled={testing} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-navy-900 font-medium px-4 py-2.5 text-sm" data-testid="settings-test-btn">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 text-blue-600" />} Test-E-Mail senden
          </button>
          {msg && (
            <span className={`text-sm ${msg.type === "ok" ? "text-emerald-700" : "text-rose-600"}`} data-testid="settings-msg">{msg.text}</span>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-navy-900">Versand-Protokoll</h3>
          <button onClick={loadLogs} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-navy-900" data-testid="settings-logs-refresh"><RefreshCw className="h-3.5 w-3.5" /> Aktualisieren</button>
        </div>
        {logs.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="settings-logs-empty">Noch keine E-Mails versendet.</p>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="settings-logs">
            {logs.map((lg, i) => {
              const st = LOG_STATUS[lg.status] || LOG_STATUS.skipped;
              return (
                <div key={i} className="py-3 flex items-start gap-3" data-testid="settings-log-row">
                  <span className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${st.cls}`}><st.icon className="h-3 w-3" /> {st.label}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-navy-900 truncate">{lg.subject}</p>
                    <p className="text-xs text-slate-500">an {lg.to || "—"} · {fmtDate(lg.created_at)}</p>
                    {lg.error && <p className="text-xs text-rose-600 mt-0.5 break-words">{lg.error}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("Alle");
  const [typFilter, setTypFilter] = useState("Alle");
  const [q, setQ] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView] = useState("anfragen");

  const load = async () => {
    const [s, l] = await Promise.all([adminStats(), adminLeads()]);
    setStats(s);
    setLeads(l);
  };

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login", { replace: true }); return; }
    adminMe()
      .then(load)
      .then(() => setReady(true))
      .catch(() => { clearToken(); navigate("/admin/login", { replace: true }); });
  }, [navigate]);

  const refresh = async () => { setRefreshing(true); try { await load(); } finally { setRefreshing(false); } };
  const logout = () => { clearToken(); navigate("/admin/login", { replace: true }); };

  const removeLead = async (lead) => {
    if (!window.confirm(`Anfrage von "${lead.name || "Ohne Namen"}" wirklich löschen? Dies kann nicht rückgängig gemacht werden.`)) return;
    try {
      await adminDeleteLead(lead.id);
      setLeads((prev) => prev.filter((x) => x.id !== lead.id));
    } catch (e) {
      alert("Löschen fehlgeschlagen. Bitte erneut versuchen.");
    }
  };

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (filter !== "Alle" && l.category !== filter) return false;
      if (typFilter !== "Alle" && l.kundentyp !== typFilter) return false;
      if (q) {
        const s = `${l.name} ${l.email} ${l.topic}`.toLowerCase();
        if (!s.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [leads, filter, typFilter, q]);

  if (!ready) {
    return <div className="min-h-screen grid place-items-center bg-slate-50" data-testid="admin-loading"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Topbar */}
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-full bg-blue-700 grid place-items-center"><Zap className="h-4 w-4 text-white" strokeWidth={2.5} /></span>
            <span className="font-heading font-semibold text-navy-900">Lead-Dashboard</span>
          </div>
          <div className="hidden sm:flex items-center gap-1 bg-slate-100 rounded-full p-1">
            <button onClick={() => setView("anfragen")} className={`text-sm rounded-full px-4 py-1.5 font-medium transition-colors ${view === "anfragen" ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-navy-900"}`} data-testid="view-anfragen">Eingehende Anfragen</button>
            <button onClick={() => setView("recherche")} className={`text-sm rounded-full px-4 py-1.5 font-medium transition-colors ${view === "recherche" ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-navy-900"}`} data-testid="view-recherche">Lead-Recherche</button>
            <button onClick={() => setView("einstellungen")} className={`text-sm rounded-full px-4 py-1.5 font-medium transition-colors inline-flex items-center gap-1.5 ${view === "einstellungen" ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-navy-900"}`} data-testid="view-einstellungen"><Settings className="h-3.5 w-3.5" /> Einstellungen</button>
          </div>
          <div className="flex items-center gap-3">
            {view === "anfragen" && (
              <button onClick={refresh} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-navy-900 rounded-full border border-slate-200 px-3 py-1.5" data-testid="admin-refresh">
                <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Aktualisieren
              </button>
            )}
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-rose-600 rounded-full border border-slate-200 px-3 py-1.5" data-testid="admin-logout">
              <LogOut className="h-3.5 w-3.5" /> Abmelden
            </button>
          </div>
        </div>
        <div className="sm:hidden max-w-[1400px] mx-auto px-6 pb-3 flex gap-1 bg-slate-100/50">
          <button onClick={() => setView("anfragen")} className={`flex-1 text-sm rounded-full px-4 py-1.5 font-medium ${view === "anfragen" ? "bg-white text-navy-900 shadow-sm" : "text-slate-500"}`}>Anfragen</button>
          <button onClick={() => setView("recherche")} className={`flex-1 text-sm rounded-full px-4 py-1.5 font-medium ${view === "recherche" ? "bg-white text-navy-900 shadow-sm" : "text-slate-500"}`}>Recherche</button>
          <button onClick={() => setView("einstellungen")} className={`flex-1 text-sm rounded-full px-4 py-1.5 font-medium ${view === "einstellungen" ? "bg-white text-navy-900 shadow-sm" : "text-slate-500"}`}>Einstellungen</button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 sm:px-10 py-8">
        {view === "recherche" ? <LeadResearch /> : view === "einstellungen" ? <SettingsView /> : (
        <>
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8" data-testid="admin-stats">
            <StatCard icon={Users} label="Anfragen gesamt" value={stats.total} accent="bg-blue-50 text-blue-700" testid="stat-total" />
            <StatCard icon={CalendarClock} label="Heute" value={stats.heute} accent="bg-blue-50 text-blue-700" testid="stat-heute" />
            <StatCard icon={Flame} label="Hohe Priorität" value={stats.hoch} accent="bg-rose-50 text-rose-600" testid="stat-hoch" />
            <StatCard icon={TrendingUp} label="Gewerbe" value={stats.gewerbe} accent="bg-amber-50 text-amber-600" testid="stat-gewerbe" />
            <StatCard icon={FileText} label="Mit Rechnung" value={stats.mit_rechnung} accent="bg-emerald-50 text-emerald-600" testid="stat-rechnung" />
            <StatCard icon={PiggyBank} label="Sparpotenzial (Σ)" value={`${stats.einsparpotenzial_gesamt} €`} accent="bg-emerald-50 text-emerald-600" testid="stat-einspar" />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex gap-1.5" data-testid="admin-filter-cat">
            {["Alle", "Hoch", "Mittel", "Niedrig"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={`text-sm rounded-full px-4 py-2 font-medium transition-colors ${filter === f ? "bg-blue-700 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"}`} data-testid={`filter-${f}`}>{f}</button>
            ))}
          </div>
          <div className="flex gap-1.5" data-testid="admin-filter-typ">
            {["Alle", "Gewerbe", "Privat"].map((f) => (
              <button key={f} onClick={() => setTypFilter(f)} className={`text-sm rounded-full px-4 py-2 font-medium transition-colors ${typFilter === f ? "bg-navy-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"}`} data-testid={`filter-typ-${f}`}>{f}</button>
            ))}
          </div>
          <div className="relative ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche Name, E-Mail…" className="bg-white border border-slate-200 rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:border-blue-400 w-64" data-testid="admin-search" />
          </div>
        </div>

        {/* Leads */}
        <div className="space-y-4" data-testid="admin-leads-list">
          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center text-slate-400" data-testid="admin-empty">Keine Anfragen für diesen Filter.</div>
          ) : (
            filtered.map((l) => <LeadCard key={`${l.source}-${l.id}`} lead={l} onDelete={removeLead} />)
          )}
        </div>
        </>
        )}
      </main>
    </div>
  );
}
